/* eslint-disable react-hooks/exhaustive-deps */
'use client';
import {
  SearchAnalyticsBoundary,
  SearchAnalyticsItem,
  type SearchAnalyticsData,
} from '@eliadmin/core/src/components/analytics/search-analytics-boundary';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';
import type { Category, Tag } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { MobileSearchBarTab } from '@eliadmin/core/src/components/header/search-bar/mobile-search-bar-tab';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ButtonIcon } from '@eliadmin/core/src/components/ui/button-icon';
import type { InputProps } from '@eliadmin/core/src/components/ui/input';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Popover, PopoverContent, PopoverAnchor } from '@eliadmin/core/src/components/ui/popover';
import { SheetTrigger } from '@eliadmin/core/src/components/ui/sheet';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { usePathname, useRouter } from 'next/navigation';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { LuSearch } from 'react-icons/lu';
import { Paragraph } from './paragraph';

type GenericItem = {
  name: string;
  slug: string;
};

export type SearchBarProps = {
  searchAnalytics?: SearchAnalyticsData;
  setSearchQuery: (query: string) => void;
  searchQuery: string;
  isLoading: boolean;
  shouldRedirect?: boolean;
  items: React.ReactNode[];
  itemsBlogs?: Blog[];
  itemsTags?: any[];
  itemsCategories?: any[];
  placeholder?: string;
  customButton?: React.ReactNode;
  desktopSearchButton?: boolean;
  mobileSearchButton?: boolean;
  disableSidebar?: boolean;
  className?: {
    root?: string;
    form?: string;
    input?: InputProps['className'];
    button?: string;
    popoverContent?: string;
    spinner?: string;
    itemsClassName?: string;
    itemClassName?: string;
    empty?: string;
    emptyText?: string;
  };
  toggle?: () => void;
};

export function SearchBar({
  className,
  setSearchQuery,
  searchQuery,
  isLoading,
  items,
  itemsBlogs,
  itemsTags,
  itemsCategories,
  placeholder,
  shouldRedirect = false,
  customButton,
  mobileSearchButton = false,
  desktopSearchButton = false,
  disableSidebar = false,
  toggle,
  searchAnalytics,
}: SearchBarProps) {
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();
  const router = useRouter();
  const pathname = usePathname();

  const [debounceFinished, setDebounceFinished] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement> | boolean = false) => {
    if (typeof e === 'object') {
      e.preventDefault();
    }

    if (!searchQuery || searchQuery === '') {
      router.push(shouldRedirect ? ROUTES.SEARCH() : `${pathname}`);
      return;
    }

    if (toggle) {
      toggle();
    }

    router.push(shouldRedirect ? ROUTES.SEARCH(searchQuery) : `${pathname}?search=${searchQuery}`);
  };

  const formRef = useRef<HTMLFormElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { isOpen, setIsOpen } = useDisclosure(false);

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  useEffect(() => {
    setDebounceFinished(false);
    const timeoutId = setTimeout(() => setDebounceFinished(true), 400);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchQuery, isLoading]);

  function handleOpenChange(open: boolean) {
    setIsOpen(open);
  }

  const itemCategories = itemsCategories as Category[] | undefined;
  const itemTags = itemsTags as Tag[] | undefined;
  const itemBlogs = itemsBlogs as Blog[] | undefined;

  const hasSidebarContent =
    !disableSidebar &&
    ((itemCategories?.length ?? 0) > 0 ||
      (itemTags?.length ?? 0) > 0 ||
      (itemBlogs?.length ?? 0) > 0);

  const tabsTriggerClassName = cn(
    'rounded-none pb-3 flex items-center justify-center border-b-[3px] border-b-transparent text-sm font-semibold tracking-tight pt-0',
    'data-[state=active]:text-primary data-[state=active]:border-b-primary data-[state=active]:shadow-none',
    'data-[state=inactive]:text-[#666666]',
  );

  const renderMobileTabs = () => (
    <Tabs
      defaultValue="products"
      className="flex h-full min-h-0 w-full flex-col gap-3 p-0 shadow-none"
    >
      <TabsList
        className={cn(
          'grid h-fit w-full grid-cols-4 gap-0 rounded-none bg-transparent p-0 shadow-none',
          'ea-search-bar-tabs__list',
        )}
      >
        <TabsTrigger value="products" className={cn(tabsTriggerClassName)}>
          {t('text150')}
        </TabsTrigger>
        <TabsTrigger value="blogs" className={cn(tabsTriggerClassName)}>
          {t('text671')}
        </TabsTrigger>
        <TabsTrigger value="tags" className={cn(tabsTriggerClassName)}>
          {t('text437')}
        </TabsTrigger>
        <TabsTrigger value="categories" className={cn(tabsTriggerClassName)}>
          {t('text176')}
        </TabsTrigger>
      </TabsList>
      <TabsContent
        value="products"
        className="mt-0 min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 shadow-none [-webkit-overflow-scrolling:touch]"
      >
        {items && (
          <SearchAnalyticsBoundary data={searchAnalytics} mode="autocomplete" active={!isLoading}>
            <div className="w-full">
              {isLoading ? (
                <div
                  className={cn(
                    'flex h-[300px] w-full items-center justify-center',
                    'ea-search-bar__spinner',
                    className?.spinner,
                  )}
                >
                  <Spinner />
                </div>
              ) : items.length ? (
                <div
                  className={cn(
                    'flex w-full flex-col items-center justify-start gap-3',
                    'ea-search-bar__items',
                    className?.itemsClassName,
                  )}
                >
                  {items.slice(0, 3).map((item, searchIndex) => (
                    <SearchAnalyticsItem index={searchIndex} key={searchIndex}>
                      {item &&
                        React.cloneElement(item as any, {
                          className: cn('', className?.itemClassName),
                        })}
                    </SearchAnalyticsItem>
                  ))}
                  {mobileSearchButton && (
                    <SheetTrigger asChild className="my-3">
                      <Button
                        variant={'white'}
                        type="button"
                        onClick={(e: any) => handleSubmit(e)}
                        className={cn(
                          'w-full sm:w-fit',
                          'ea-search-bar__button',
                          className?.button,
                        )}
                      >
                        {t('text670')}
                      </Button>
                    </SheetTrigger>
                  )}
                </div>
              ) : (
                <div className={cn('flex flex-col p-4', 'ea-search-bar__empty', className?.empty)}>
                  <p className={cn('text-sm', 'ea-search-bar__empty-text', className?.emptyText)}>
                    {t('text554')}
                  </p>
                </div>
              )}
            </div>
          </SearchAnalyticsBoundary>
        )}
      </TabsContent>
      <TabsContent
        value="blogs"
        className="mt-0 min-h-0 flex-1 overflow-y-auto overscroll-contain shadow-none [-webkit-overflow-scrolling:touch]"
      >
        <MobileSearchBarTab type={'blogs'} items={itemsBlogs} toggle={toggle} />
      </TabsContent>
      <TabsContent
        value="tags"
        className="mt-0 min-h-0 flex-1 overflow-y-auto overscroll-contain shadow-none [-webkit-overflow-scrolling:touch]"
      >
        <MobileSearchBarTab type={'tags'} items={itemsTags} toggle={toggle} />
      </TabsContent>
      <TabsContent
        value="categories"
        className="mt-0 min-h-0 flex-1 overflow-y-auto overscroll-contain shadow-none [-webkit-overflow-scrolling:touch]"
      >
        <MobileSearchBarTab type={'categories'} items={itemsCategories} toggle={toggle} />
      </TabsContent>
    </Tabs>
  );

  if (mobileSearchButton) {
    return (
      <div className={cn('ea-search-bar flex h-full min-h-0 w-full flex-col', className?.root)}>
        <form
          role="search"
          aria-label={systemLabels('search')}
          ref={formRef}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(e);
            toggle && toggle();
          }}
          className={cn('flex w-full flex-row items-center', 'ea-search-bar__form')}
        >
          <Input
            aria-label={systemLabels('search')}
            before={<FiSearch className="ml-2 h-4 w-4 text-muted-foreground" />}
            value={searchQuery}
            placeholder={placeholder ?? t('text162')}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant={HEADER_CONFIG.searchBar.inputVariant}
            className={{
              ...className?.input,
              wrapper: cn(
                'border-e-none h-12 rounded-lg pr-1',
                'ea-search-bar__input',
                className?.input?.wrapper,
              ),
            }}
            after={
              customButton || (
                <Button
                  aria-label={systemLabels('search')}
                  variant={HEADER_CONFIG.searchBar.buttonVariant}
                  className={cn(
                    'h-10 w-10 rounded-lg border-none p-0',
                    'ea-search-bar__button',
                    className?.button,
                  )}
                >
                  <LuSearch className="h-5 w-5" />
                </Button>
              )
            }
          />
        </form>
        {searchQuery.length > 1 && (
          <div
            className={cn(
              'mt-2 min-h-0 flex-1 overflow-hidden',
              'ea-search-bar__popover-content',
              className?.popoverContent,
            )}
          >
            {renderMobileTabs()}
          </div>
        )}
      </div>
    );
  }

  return (
    <Popover open={isOpen && searchQuery.length > 1} onOpenChange={handleOpenChange}>
      <PopoverAnchor
        onChangeCapture={() => setIsOpen(true)}
        onFocusCapture={() => setIsOpen(true)}
        asChild
        className={cn('w-full', 'ea-search-bar', className?.root)}
      >
        <form
          role="search"
          aria-label={systemLabels('search')}
          ref={formRef}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(e);
            toggle && toggle();
          }}
          className={cn('flex w-full flex-row items-center', 'ea-search-bar__form')}
        >
          <Input
            aria-label={systemLabels('search')}
            value={searchQuery}
            placeholder={placeholder ?? t('text162')}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant={HEADER_CONFIG.searchBar.inputVariant}
            className={{
              main: cn('border-none px-4'),
              wrapper: cn(
                'border-e-none ea-rounded-[16px] h-[50px] border pr-1',
                'ea-search-bar__input',
                className?.input?.wrapper,
              ),
            }}
            after={
              customButton || (
                <Button
                  aria-label={systemLabels('search')}
                  variant={HEADER_CONFIG.searchBar.buttonVariant}
                  className={cn(
                    'ea-search-bar__button h-[42px] rounded-[12px] font-medium sm:h-[42px]',
                    className?.button,
                  )}
                >
                  <ButtonIcon
                    alt={t('text1')}
                    url="/images/icons/search-white.svg"
                    className="max-h-[22px] min-h-[22px] min-w-[22px] max-w-[22px]"
                  />
                  {t('text1')}
                </Button>
              )
            }
          />
        </form>
      </PopoverAnchor>
      <SearchAnalyticsBoundary data={searchAnalytics} mode="autocomplete" active={!isLoading}>
        <PopoverContent
          onInteractOutside={(e) => {
            if (formRef.current?.contains(e.target as Node)) e.preventDefault();
          }}
          onCloseAutoFocus={(e) => e.preventDefault()}
          aria-label={systemLabels('searchResults')}
          sideOffset={20}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cn(
            'hidden w-[1400px] max-w-[100vw] rounded-[20px] border px-0 [box-shadow:0px_4px_16px_0px_rgba(0,0,0,0.06),0px_12px_40px_-4px_rgba(0,0,0,0.10)] sm:block',
            'ea-search-bar__popover-content',
            className?.popoverContent,
          )}
          ref={contentRef}
        >
          {(!debounceFinished || isLoading) && searchQuery !== '' && (
            <div
              className={cn(
                'custom-container',
                'mx-auto flex h-[96px] w-full items-center justify-center bg-white',
                'ea-search-bar__spinner',
                className?.spinner,
              )}
            >
              <Spinner className="h-6 w-6 sm:h-8 sm:w-8" />
            </div>
          )}
          {debounceFinished &&
            !isLoading &&
            searchQuery !== '' &&
            !items?.length &&
            !hasSidebarContent && (
              <div className="custom-container mx-auto flex h-[96px] w-full items-center justify-center bg-white">
                <Paragraph color="muted-foreground" className="w-full text-center" size="lg">
                  Žiadne výsledky neboli nájdené
                </Paragraph>
              </div>
            )}
          {items?.length > 0 && !(!debounceFinished || isLoading) && searchQuery !== '' && (
            <div
              className={cn(
                'custom-container',
                'mx-auto flex flex-col items-start justify-start gap-4 bg-white p-4 sm:p-5 md:flex-row md:items-stretch md:justify-between lg:gap-6 lg:p-6',
              )}
            >
              {items.length ? (
                <div
                  className={cn(
                    'flex w-full max-w-[924px] flex-col items-start justify-start gap-3',
                    'ea-search-bar__items',
                    className?.itemsClassName,
                  )}
                >
                  {items.slice(0, 4).map((item, searchIndex) => (
                    <SearchAnalyticsItem index={searchIndex} key={searchIndex}>
                      {item &&
                        React.cloneElement(item as any, {
                          className: cn('', className?.itemClassName),
                        })}
                    </SearchAnalyticsItem>
                  ))}
                  {desktopSearchButton && items.length > 0 && (
                    <div className="mt-3 flex w-full items-center justify-center">
                      <Button
                        aria-label={systemLabels('search')}
                        variant="white"
                        className={cn('w-full sm:w-fit', className?.button)}
                        onClick={() => {
                          toggle?.();
                        }}
                      >
                        <Link href={ROUTES.SEARCH(searchQuery)}>{t('text670')}</Link>
                      </Button>
                    </div>
                  )}
                </div>
              ) : null}
              {hasSidebarContent && (
                <div className="w-full md:max-w-[280px] lg:max-w-[403px]">
                  <div className="flex h-full w-full flex-col items-start justify-start gap-6 rounded-[20px] bg-[#F3F4F6] p-4 sm:p-5 lg:p-6">
                    <GenericItems items={itemCategories} type="categories" onNavigate={toggle} />
                    <GenericItems
                      items={itemTags as GenericItem[] | undefined}
                      type="tags"
                      onNavigate={toggle}
                    />
                    <GenericItems
                      items={itemBlogs as GenericItem[]}
                      type="blogs"
                      onNavigate={toggle}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </PopoverContent>
      </SearchAnalyticsBoundary>
    </Popover>
  );
}

function GenericItems({
  items,
  type,
  onNavigate,
}: {
  items: GenericItem[] | undefined;
  type: 'categories' | 'tags' | 'blogs';
  onNavigate?: () => void;
}) {
  const { t } = useTranslations();

  if (!items?.length) return null;

  const title =
    type === 'categories' ? t('text176') : type === 'tags' ? t('text437') : t('text671');

  return (
    <div className="flex w-full flex-col items-start justify-start gap-2">
      <p className="text-sm font-medium tracking-tighter text-foreground">{title}</p>
      {items.map((item, searchIndex) => (
        <SearchAnalyticsItem index={searchIndex} key={searchIndex}>
          {
            <Link
              key={item.name}
              prefetch={false}
              href={item.slug}
              onClick={() => onNavigate?.()}
              className="generic-hover line-clamp-2 text-sm font-medium text-primary lg:text-base"
            >
              {item.name}
            </Link>
          }
        </SearchAnalyticsItem>
      ))}
    </div>
  );
}
